<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Toast 状态
const showToast = ref(false);
const toastMessage = ref('');
const toastType = ref('error'); // 'error', 'success', 'warning'
const toastDuration = ref(3000);
let toastTimer: number | null = null;

// 显示 Toast 消息
const showToastMessage = (message: string, type: string = 'error', duration: number = 3000) => {
  // 如果已经有 toast 在显示，先清除定时器
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
  
  // 设置新的 toast 消息
  toastMessage.value = message;
  toastType.value = type;
  toastDuration.value = duration;
  showToast.value = true;
  
  // 设置自动关闭定时器
  toastTimer = window.setTimeout(() => {
    showToast.value = false;
    toastTimer = null;
  }, duration);
};

// 关闭 Toast
const closeToast = () => {
  showToast.value = false;
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
};

// 暴露方法给外部使用
defineExpose({
  showToast: showToastMessage,
  closeToast
});

// 监听全局事件
onMounted(() => {
  // 使用命名函数便于移除事件监听器
  window.addEventListener('show-toast', handleShowToastEvent);
});

// 使用同一个函数引用来添加和移除事件监听器
const handleShowToastEvent = ((event: CustomEvent) => {
  const { message, type, duration } = event.detail;
  showToastMessage(message, type, duration);
}) as EventListener;

onUnmounted(() => {
  // 正确移除事件监听器，使用相同的函数引用
  window.removeEventListener('show-toast', handleShowToastEvent);
  
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
});
</script>

<template>
  <Transition name="toast">
    <div 
      v-if="showToast" 
      class="fixed top-16 left-1/2 lg:left-[calc(50%_+_100px)] transform -translate-x-1/2 z-[10000] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 max-w-md"
      :class="{
        'bg-red-100 text-red-800 dark:bg-red-900/70 dark:text-red-200': toastType === 'error',
        'bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-200': toastType === 'success',
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/70 dark:text-yellow-200': toastType === 'warning'
      }"
    >
      <div 
        :class="{
          'i-carbon-error-filled text-red-500 dark:text-red-400': toastType === 'error',
          'i-carbon-checkmark-filled text-green-500 dark:text-green-400': toastType === 'success',
          'i-carbon-warning-filled text-yellow-500 dark:text-yellow-400': toastType === 'warning'
        }"
      ></div>
      <span>{{ toastMessage }}</span>
      <button 
        @click="closeToast"
        class="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
      >
        <div class="i-carbon-close text-sm"></div>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
/* Toast 动画 */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -20px);
}
</style> 